import React from 'react';
import {
  Box,
  Typography,
  Container,
  Stack,
  Button,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { UserManagementProvider, useUserManagement } from '../context/UserManagementContext';
import { useUserManagementOperations } from '../hooks/useUserManagement';
import { useToast } from '../components/ToastNotification';
import { UserSearchBar } from '../components/UserSearchBar';
import { UserGrid } from '../components/UserGrid';
import { UserEditSidebar } from '../components/UserEditSidebar';
import { UserDeleteModal } from '../components/UserDeleteModal';
import { UserDisableModal } from '../components/UserDisableModal';
import { User } from '../services/userManagementService';

function UserManagementContent() {
  const { state, dispatch, openEditSidebar, closeEditSidebar } = useUserManagement();
  const {
    userCounts,
    loading,
    error,
    selectedUsers,
    updateUser,
    updateUserStatus,
    deleteUser,
    performBulkAction,
    goToPage,
    changePageSize,
  } = useUserManagementOperations();
  
  const { showSuccess, showError } = useToast();

  const handleSearch = async (query: string, filters: { role?: 'PATIENT' | 'CAREGIVER' | 'ADMIN'; disabled?: boolean }) => {
    // Search is handled by the hook automatically
  };

  const handleEditUser = (user: User) => {
    openEditSidebar(user);
  };

  const handleSaveUser = async (user: User, updates: Partial<User>) => {
    const result = await updateUser(user.id, updates);
    if (result.success) {
      showSuccess(result.message);
    } else {
      showError(result.message);
    }
    return result;
  };

  const handleDeleteUser = async (user: User) => {
    const result = await deleteUser(user.id);
    if (result.success) {
      showSuccess(result.message);
    } else {
      showError(result.message);
    }
    return result;
  };

  const handleDisableUser = async (user: User) => {
    const result = await updateUserStatus(user.id, true);
    if (result.success) {
      showSuccess(result.message);
    } else {
      showError(result.message);
    }
    return result;
  };

  const handleEnableUser = async (user: User) => {
    const result = await updateUserStatus(user.id, false);
    if (result.success) {
      showSuccess(result.message);
    } else {
      showError(result.message);
    }
    return result;
  };

  const handleBulkDelete = async () => {
    const result = await performBulkAction('delete');
    if (result.success) {
      showSuccess(result.message);
    } else {
      showError(result.message);
    }
  };

  const handleBulkDisable = async () => {
    const result = await performBulkAction('disable');
    if (result.success) {
      showSuccess(result.message);
    } else {
      showError(result.message);
    }
  };

  const handleBulkEnable = async () => {
    const result = await performBulkAction('enable');
    if (result.success) {
      showSuccess(result.message);
    } else {
      showError(result.message);
    }
  };

  const handleRefresh = () => {
    window.location.reload(); // Simple refresh for now
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              User Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage users, roles, and permissions across the platform
            </Typography>
          </Box>
          
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled
            >
              Add User
            </Button>
          </Stack>
        </Stack>

        {/* Stats */}
        {userCounts && (
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Chip
              label={`${userCounts.total} Total Users`}
              color="primary"
              variant="outlined"
            />
            <Chip
              label={`${userCounts.byRole.patients} Patients`}
              color="info"
              variant="outlined"
            />
            <Chip
              label={`${userCounts.byRole.caregivers} Caregivers`}
              color="warning"
              variant="outlined"
            />
            <Chip
              label={`${userCounts.byRole.admins} Admins`}
              color="error"
              variant="outlined"
            />
            <Chip
              label={`${userCounts.active} Active`}
              color="success"
              variant="outlined"
            />
            <Chip
              label={`${userCounts.disabled} Disabled`}
              color="default"
              variant="outlined"
            />
          </Stack>
        )}
      </Box>

      {/* Search */}
      <UserSearchBar onSearch={handleSearch} loading={loading} />

      {/* Bulk Actions */}
      {selectedUsers.length > 0 && (
        <Box mb={3}>
          <Alert severity="info" sx={{ mb: 2 }}>
            {selectedUsers.length} user(s) selected
          </Alert>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<BlockIcon />}
              onClick={handleBulkDisable}
              disabled={loading}
            >
              Disable Selected
            </Button>
            <Button
              variant="outlined"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={handleBulkEnable}
              disabled={loading}
            >
              Enable Selected
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleBulkDelete}
              disabled={loading}
            >
              Delete Selected
            </Button>
          </Stack>
        </Box>
      )}

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading Overlay */}
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      )}

      {/* User Grid */}
      {!loading && (
        <UserGrid
          onEditUser={handleEditUser}
          onDeleteUser={handleDeleteUser}
          onDisableUser={handleDisableUser}
          onEnableUser={handleEnableUser}
          onPageChange={goToPage}
          onRowsPerPageChange={changePageSize}
          loading={loading}
          error={error}
        />
      )}

      {/* Modals and Sidebar */}
      <UserEditSidebar
        open={state.showEditSidebar}
        // Clear editingUser on close so the embedded Trust Score
        // section unmounts (FEAT-043-admin-web privacy rule — no
        // cache, fresh read-reason on every re-open).
        onClose={closeEditSidebar}
        onSave={handleSaveUser}
      />

      <UserDeleteModal
        open={state.showDeleteModal}
        onClose={() => dispatch({ type: 'SET_SHOW_DELETE_MODAL', payload: false })}
        onConfirm={handleDeleteUser}
      />

      <UserDisableModal
        open={state.showDisableModal}
        onClose={() => dispatch({ type: 'SET_SHOW_DISABLE_MODAL', payload: false })}
        onConfirm={(user, disabled) => 
          disabled ? handleDisableUser(user) : handleEnableUser(user)
        }
      />
    </Container>
  );
}

export function UserManagementPage() {
  return (
    <UserManagementProvider>
      <UserManagementContent />
    </UserManagementProvider>
  );
}
